import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, ChevronDown, ChevronUp } from 'lucide-react';
import './DragDropUpload.scss';

function DragAndDropUpload({
  files,
  onFileChange,
  multiple = false,
  disabled = false,
  acceptDocx = false,
  acceptZip = false
}) {
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const inputRef = useRef(null);

  // Maximum number of files to show without collapsing
  const MAX_VISIBLE_FILES = 5;

  const handleFileChange = selectedFiles => {
    if (multiple) {
      // Pass the FileList to parent
      onFileChange(selectedFiles);
    } else {
      // Handle single file
      const selectedFile = selectedFiles instanceof FileList ? selectedFiles[0] : selectedFiles;
      onFileChange(selectedFile);
    }
  };

  const removeFile = indexToRemove => {
    // Even when component is disabled, allow file removal
    if (multiple && files) {
      const updatedFiles = files.filter((_, index) => index !== indexToRemove);
      onFileChange(updatedFiles);
    } else {
      onFileChange(null);
    }
  };

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileChange(multiple ? e.dataTransfer.files : e.dataTransfer.files[0]);
      }
      setDragging(false);
    },
    [multiple]
  );

  const handleDrag = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragOut = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const triggerFileInput = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className={`upload-container ${disabled ? 'disabled' : ''}`}>
      <div
        className={`upload-box ${dragging ? 'dragging' : ''} ${hover ? 'hover' : ''} ${(Array.isArray(files) ? files.length > 0 : !!files) ? 'uploaded' : ''}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onDrop={disabled ? null : handleDrop}
        onDragOver={disabled ? null : handleDrag}
        onDragEnter={disabled ? null : handleDragIn}
        onDragLeave={disabled ? null : handleDragOut}
        onClick={triggerFileInput}
      >
        <div className="upload-prompt">
          <Upload className="upload-icon" size={45} />
          <span className="upload-text">
            {disabled ? 'Upload disabled during processing' : 'Drag & drop files here or'}
          </span>
          <button
            className="browse-button"
            disabled={disabled}
            onClick={e => {
              e.stopPropagation();
              triggerFileInput();
            }}
          >
            Browse Files
          </button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={[
            'application/pdf',
            acceptDocx && 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            acceptZip && 'application/zip',
            acceptZip && 'application/x-zip-compressed'
          ]
            .filter(Boolean)
            .join(',')}
          name="file_upload"
          className="file-input"
          multiple={multiple}
          disabled={disabled}
          onClick={e => {
            if (disabled) {
              e.preventDefault();
            } else {
              // Clear the value so the same file can be selected again
              e.target.value = null;
            }
          }}
          onChange={e => {
            if (!disabled && e.target.files && e.target.files.length > 0) {
              handleFileChange(multiple ? e.target.files : e.target.files[0]);
            }
          }}
        />
      </div>

      {/* File display section */}
      {files && (
        <div className="files-display">
          {!multiple && files && (
            <div className="single-file">
              <div className="file-name" title={files.name}>
                {files.name.length > 80 ? files.name.substring(0, 80) + '...' : files.name}
              </div>
              <button
                className="remove-file"
                onClick={e => {
                  e.preventDefault();
                  removeFile(0);
                }}
              >
                <X size={14} />
              </button>
            </div>
          )}

          {multiple && files.length > 0 && (
            <div className="multiple-files">
              <div className="files-header">
                <div className="files-count">
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </div>
                {files.length > MAX_VISIBLE_FILES && (
                  <button className="toggle-files" onClick={() => setShowAllFiles(!showAllFiles)}>
                    {showAllFiles ? (
                      <>
                        <span>Show less</span>
                        <ChevronUp size={14} />
                      </>
                    ) : (
                      <>
                        <span>Show all (+{files.length - MAX_VISIBLE_FILES})</span>
                        <ChevronDown size={14} />
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="files-list">
                {files
                  .slice(0, showAllFiles ? files.length : Math.min(files.length, MAX_VISIBLE_FILES))
                  .map((file, index) => (
                    <div key={`${file.name}-${index}`} className="file-item">
                      <div className="file-name" title={file.name}>
                        {file.name.length > 80 ? file.name.substring(0, 80) + '...' : file.name}
                      </div>
                      <button
                        className="remove-file"
                        disabled={disabled}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DragAndDropUpload;
